import type {
  ApiResponse,
  Member,
  MemberHousehold,
  Membership,
  MembershipAddon,
  MembershipAddonSelection,
  MembershipPaymentMethod,
  MembershipSeat,
  MembershipType,
  PaginatedResponse,
} from '@court-sync/shared';
import type { PaginationState } from '@/components/ui/PaginatedTable';
import { apiClient, apiDelete, apiGet, apiPatch, apiPost, isApiError } from './apiClient';

/** One covered seat in a quote/purchase/assign request — mirrors `membershipSeatMemberInputSchema`. */
export type MembershipSeatInput =
  | { kind: 'existing'; memberId: string }
  | {
      kind: 'new';
      firstName: string;
      lastName: string;
      email?: string | null;
      phone?: string | null;
      dateOfBirth?: string | null;
    };

export interface MembershipAddonSelectionInput {
  addonId: string;
  memberId?: string | null;
  quantity?: number;
}

export interface MembershipQuoteRequest {
  membershipTypeId: string;
  seats: MembershipSeatInput[];
  addonSelections: MembershipAddonSelectionInput[];
}

export interface MembershipPurchaseRequest extends MembershipQuoteRequest {
  householdId?: string | null;
  acknowledgeSurcharge?: boolean;
}

export interface MembershipQuoteSeat {
  memberId: string | null;
  firstName: string;
  lastName: string;
  seatType: 'adult' | 'junior' | 'associate';
  isPrimary: boolean;
}

export interface MembershipQuoteBreakdown {
  basePriceCents: number;
  addonsTotalCents: number;
  surchargeCents: number;
  totalCents: number;
  resolvedAddonSelections: { unitPriceCents: number; quantity: number; totalCents: number }[];
}

export interface MembershipQuoteResponse {
  breakdown: MembershipQuoteBreakdown;
  period: { startDate: string; endDate: string | null; seasonLabel: string | null };
  requiresVerification: boolean;
  verificationReasons: string[];
  seats: MembershipQuoteSeat[];
}

export interface MembershipPurchaseResponse {
  membershipId: string;
  status: Membership['status'];
  clientSecret: string | null;
  paymentIntentId: string | null;
  subscriptionId: string | null;
  stripeConnectAccountId: string;
  totalCents: number;
  currency: string;
}

export interface MembershipDetailResponse {
  membership: Membership;
  seats: MembershipSeat[];
  addonSelections: MembershipAddonSelection[];
}

/** Generic wrapper matching the `lessonEnrollment.ts` service convention: never throws, always resolves. */
async function safeCall<T>(fn: () => Promise<ApiResponse<T>>, fallbackError: string): Promise<ApiResponse<T>> {
  try {
    return await fn();
  } catch (error: unknown) {
    return {
      success: false,
      error: isApiError(error) ? error.message : error instanceof Error ? error.message : fallbackError,
    };
  }
}

export const clubMembersService = {
  async listMembers(
    clubId: string,
    params?: {
      page?: number;
      limit?: number;
      search?: string;
      householdId?: string;
      isActive?: boolean;
    }
  ): Promise<ApiResponse<Member[]> | PaginatedResponse<Member>> {
    return apiGet<Member[]>(`/members/${clubId}/members`, { params });
  },

  async getMember(clubId: string, memberId: string): Promise<ApiResponse<Member>> {
    return apiGet<Member>(`/members/${clubId}/members/${memberId}`);
  },

  async createMember(clubId: string, data: Partial<Member>): Promise<ApiResponse<{ id: string }>> {
    return apiPost<{ id: string }>(`/members/${clubId}/members`, data);
  },

  async updateMember(
    clubId: string,
    memberId: string,
    data: Partial<Member>
  ): Promise<ApiResponse<null>> {
    return apiPatch<null>(`/members/${clubId}/members/${memberId}`, data);
  },

  async deactivateMember(clubId: string, memberId: string): Promise<ApiResponse<null>> {
    return apiDelete<null>(`/members/${clubId}/members/${memberId}`);
  },

  async listMembershipTypes(clubId: string): Promise<ApiResponse<MembershipType[]>> {
    return apiGet<MembershipType[]>(`/members/${clubId}/membership-types`);
  },

  async createMembershipType(
    clubId: string,
    data: Partial<MembershipType>
  ): Promise<ApiResponse<{ id: string }>> {
    return apiPost<{ id: string }>(`/members/${clubId}/membership-types`, data);
  },

  async updateMembershipType(
    clubId: string,
    typeId: string,
    data: Partial<MembershipType>
  ): Promise<ApiResponse<null>> {
    return apiPatch<null>(`/members/${clubId}/membership-types/${typeId}`, data);
  },

  async deactivateMembershipType(clubId: string, typeId: string): Promise<ApiResponse<null>> {
    return apiDelete<null>(`/members/${clubId}/membership-types/${typeId}`);
  },

  async listMembershipAddons(clubId: string): Promise<ApiResponse<MembershipAddon[]>> {
    return apiGet<MembershipAddon[]>(`/members/${clubId}/membership-addons`);
  },

  async createMembershipAddon(
    clubId: string,
    data: Partial<MembershipAddon>
  ): Promise<ApiResponse<{ id: string }>> {
    return apiPost<{ id: string }>(`/members/${clubId}/membership-addons`, data);
  },

  async updateMembershipAddon(
    clubId: string,
    addonId: string,
    data: Partial<MembershipAddon>
  ): Promise<ApiResponse<null>> {
    return apiPatch<null>(`/members/${clubId}/membership-addons/${addonId}`, data);
  },

  async deactivateMembershipAddon(clubId: string, addonId: string): Promise<ApiResponse<null>> {
    return apiDelete<null>(`/members/${clubId}/membership-addons/${addonId}`);
  },

  // ─── Households ────────────────────────────────────────────────────────────

  async listHouseholds(clubId: string): Promise<ApiResponse<MemberHousehold[]>> {
    return apiGet<MemberHousehold[]>(`/members/${clubId}/households`);
  },

  async createHousehold(
    clubId: string,
    data: { name?: string | null }
  ): Promise<ApiResponse<{ id: string }>> {
    return apiPost<{ id: string }>(`/members/${clubId}/households`, data);
  },

  async updateHousehold(
    clubId: string,
    householdId: string,
    data: { name?: string | null }
  ): Promise<ApiResponse<null>> {
    return apiPatch<null>(`/members/${clubId}/households/${householdId}`, data);
  },

  async deleteHousehold(clubId: string, householdId: string): Promise<ApiResponse<null>> {
    return apiDelete<null>(`/members/${clubId}/households/${householdId}`);
  },

  // ─── Memberships ────────────────────────────────────────────────────────────

  async listMemberships(
    clubId: string,
    filters?: {
      status?: Membership['status'];
      householdId?: string;
      seasonLabel?: string;
      search?: string;
      page?: number;
      limit?: number;
    }
  ): Promise<ApiResponse<Membership[]> | PaginatedResponse<Membership>> {
    return apiGet<Membership[]>(`/members/${clubId}/memberships`, { params: filters });
  },

  async listRenewalsDue(clubId: string, windowDays = 30): Promise<ApiResponse<Membership[]>> {
    return apiGet<Membership[]>(`/members/${clubId}/memberships/renewals-due`, { params: { windowDays } });
  },

  async getMembership(clubId: string, membershipId: string): Promise<ApiResponse<MembershipDetailResponse>> {
    return apiGet<MembershipDetailResponse>(`/members/${clubId}/memberships/${membershipId}`);
  },

  /** Pricing/eligibility preview only — never persists, never touches Stripe. */
  async quoteMembership(
    clubId: string,
    input: MembershipQuoteRequest
  ): Promise<ApiResponse<MembershipQuoteResponse>> {
    return safeCall(
      () => apiPost<MembershipQuoteResponse>(`/members/${clubId}/memberships/quote`, input),
      'Failed to build membership quote'
    );
  },

  /** Staff paperwork: creates a pending_payment/unpaid membership without touching Stripe. */
  async assignMembership(
    clubId: string,
    input: MembershipPurchaseRequest
  ): Promise<ApiResponse<{ membershipId: string }>> {
    return safeCall(
      () => apiPost<{ membershipId: string }>(`/members/${clubId}/memberships/assign`, input),
      'Failed to assign membership'
    );
  },

  /** Club-owner only: creates the Stripe PaymentIntent/Subscription and persists the membership. */
  async purchaseMembership(
    clubId: string,
    input: MembershipPurchaseRequest
  ): Promise<ApiResponse<MembershipPurchaseResponse>> {
    return safeCall(
      () => apiPost<MembershipPurchaseResponse>(`/members/${clubId}/memberships/purchase`, input),
      'Failed to purchase membership'
    );
  },

  /** Club-owner only: records a cash / EFTPOS / bank transfer payment already collected in person. */
  async recordOfflinePayment(
    clubId: string,
    data: { membershipId: string; paymentMethod: Exclude<MembershipPaymentMethod, 'stripe'>; note?: string }
  ): Promise<ApiResponse<Membership>> {
    return safeCall(
      () => apiPost<Membership>(`/members/${clubId}/memberships/record-payment`, data),
      'Failed to record membership payment'
    );
  },

  /** Club-owner only: approve activates the membership; reject cancels it and may issue a refund. */
  async verifyMembershipProof(
    clubId: string,
    data: { membershipId: string; approve: boolean; note?: string }
  ): Promise<ApiResponse<Membership>> {
    return safeCall(
      () => apiPost<Membership>(`/members/${clubId}/memberships/verify-proof`, data),
      'Failed to verify membership'
    );
  },

  /** Club-owner only: starts a new Stripe payment for the next fixed-period term. */
  async renewMembership(
    clubId: string,
    data: { membershipId: string; addonSelections?: MembershipAddonSelectionInput[] }
  ): Promise<ApiResponse<MembershipPurchaseResponse>> {
    return safeCall(
      () => apiPost<MembershipPurchaseResponse>(`/members/${clubId}/memberships/renew`, data),
      'Failed to renew membership'
    );
  },

  /** Club-owner only: cancels the membership and optionally refunds. */
  async cancelMembership(
    clubId: string,
    data: { membershipId: string; reason?: string; refund?: boolean }
  ): Promise<ApiResponse<Membership>> {
    return safeCall(
      () => apiPost<Membership>(`/members/${clubId}/memberships/cancel`, data),
      'Failed to cancel membership'
    );
  },

  async sendClubNotice(
    clubId: string,
    data: {
      title: string;
      body: string;
      memberIds?: string[];
      channels?: { inApp: boolean; email: boolean };
      /** @deprecated Use channels.email */
      sendEmail?: boolean;
    }
  ): Promise<ApiResponse<{ id: string }>> {
    return safeCall(
      () => apiPost<{ id: string }>(`/members/${clubId}/notices`, data),
      'Failed to send club notice'
    );
  },

  async getClubNoticeHistory(
    clubId: string,
    page = 1,
    limit = 20
  ): Promise<
    ApiResponse<
      Array<{
        id: string;
        title: string;
        body: string;
        publishedAt: string;
        emailSentAt: string | null;
        emailedCount: number;
        channels: { inApp: boolean; email: boolean };
        targetMemberIds: string[] | null;
        targetSummary: {
          memberCount: number;
          recipients?: Array<{ memberId: string; name: string }>;
        } | null;
        senderName?: string;
      }>
    > & { pagination?: PaginationState }
  > {
    const response = await apiClient.get(`/members/${clubId}/notices`, {
      params: { page, limit },
    });
    return response.data;
  },

  async getClubNoticeDetail(
    clubId: string,
    noticeId: string
  ): Promise<
    ApiResponse<{
      id: string;
      title: string;
      body: string;
      publishedAt: string;
      emailSentAt: string | null;
      emailedCount: number;
      channels: { inApp: boolean; email: boolean };
      targetMemberIds: string[] | null;
      targetSummary: {
        memberCount: number;
        recipients?: Array<{ memberId: string; name: string }>;
      } | null;
      senderName?: string;
    }>
  > {
    return apiGet(`/members/${clubId}/notices/${noticeId}`);
  },
};
